import React, { useMemo } from "react";
import { TextField, Button, MenuItem, Select, FormControl, Autocomplete } from "@mui/material";
import { Search, Refresh } from "@mui/icons-material";
import styles from "./FilterBar.module.css";
import trackAnalytics from "trackAnalytics";

const statusOptions = [
    { value: "moving", label: "فى الطريق" },
    { value: "arrived", label: "وصل" },
];

const tripNumberOptions = [
    { value: "truckPlateNumberCont", label: "رقم العربيه" },
    { value: "driverPhoneNumberCont", label: "رقم السواق" },
    { value: "driverNameCont", label: "اسم السواق" },
    { value: "idCont", label: "رقم الرحلة" },
];

const FilterBar = ({
    setSearch,
    selectedFilters,
    setSelectedFilters,
    search,
    clients,
    trucksList,
    setClientInputValue,
    clientInputValue,
    truckInputValue,
    setTruckInputValue,
}) => {
    const isResetAvailable = useMemo(() => {
        return (
            search.trim() !== "" ||
            Object.values(selectedFilters).some((val) => val && val.length > 0)
        );
    }, [search, selectedFilters]);

    const handleFilterChange = (key, value, label) => {
        setSelectedFilters((prev) => ({
            ...prev,
            [key]: value || "",
        }));

        switch (key) {
            case "customers":
                trackAnalytics("filter_client_name_live_location", {
                    SelectedValue: value,
                    SelectedLabel: label,
                });
                break;
            case "statuses":
                trackAnalytics("filter_trip_status_live_location", {
                    SelectedValue: value,
                    SelectedLabel: label,
                });
                break;
            case "vehicles":
                trackAnalytics("filter_car_type_live_location", {
                    SelectedValue: value,
                    SelectedLabel: label,
                });
                break;
            case "tripNumber":
                trackAnalytics("search_type_live_trips", {
                    SelectedValue: value,
                    SelectedLabel: label,
                });
                break;
            default:
                break;
        }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        trackAnalytics("search_live_trips", { SearchValue: e.target.value });
    };

    const handleReset = () => {
        setSearch("");
        setSelectedFilters({
            customers: "",
            statuses: "",
            vehicles: "",
            tripNumber: "",
        });
    };

    const getLabel = (options, value, placeholder) => {
        const option = options.find((opt) => opt.value === value);
        return option ? option.label : <em>{placeholder}</em>;
    };

    return (
        <div className={styles.filterBarContainer}>
            <Button
                variant="contained"
                color="secondary"
                startIcon={<Refresh />}
                onClick={handleReset}
                style={{
                    backgroundColor: isResetAvailable ? "#000000" : "#FAFAFA",
                    color: isResetAvailable ? "#FAFAFA" : "#A0A0A0",
                }}
            >
                مسح
            </Button>

            <Autocomplete
                size="small"
                options={clients || []}
                getOptionLabel={(client) => client.attributes.name}
                value={clients?.find((client) => client.id === selectedFilters.customers) || null}
                inputValue={clientInputValue}
                onInputChange={(_, newInputValue) => {
                    setClientInputValue(newInputValue);
                }}
                onChange={(event, newValue) =>
                    handleFilterChange(
                        "customers",
                        newValue?.id || "",
                        newValue?.attributes.name || ""
                    )
                }
                style={{ minWidth: "115px", marginInlineEnd: "15px" }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="كل العملاء"
                        variant="outlined"
                        sx={{
                            "& .MuiInputBase-input::placeholder": { color: "black", opacity: 1 },
                        }}
                    />
                )}
            />

            <FormControl size="small">
                <Select
                    displayEmpty
                    value={selectedFilters.statuses || ""}
                    onChange={(e) => {
                        const selectedOption = statusOptions.find(
                            (opt) => opt.value === e.target.value
                        );
                        handleFilterChange("statuses", e.target.value, selectedOption?.label || "");
                    }}
                    renderValue={(selected) => getLabel(statusOptions, selected, "كل الحالات")}
                    style={{ minWidth: "90px" }}
                >
                    <MenuItem value="">كل الحالات</MenuItem>
                    {statusOptions.map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Autocomplete
                size="small"
                options={trucksList || []}
                inputValue={truckInputValue}
                onInputChange={(_, newInputValue) => {
                    setTruckInputValue(newInputValue);
                }}
                getOptionLabel={(truck) => truck.attributes.alias}
                value={trucksList?.find((truck) => truck.id === selectedFilters.vehicles) || null}
                onChange={(event, newValue) =>
                    handleFilterChange(
                        "vehicles",
                        newValue?.id || "",
                        newValue?.attributes.alias || ""
                    )
                }
                style={{ minWidth: "115px", marginInlineEnd: "15px" }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="كل العربيات"
                        variant="outlined"
                        sx={{
                            "& .MuiInputBase-input::placeholder": { color: "black", opacity: 1 },
                        }}
                    />
                )}
            />

            <TextField
                variant="outlined"
                size="normal"
                placeholder="ابحث"
                value={search}
                onChange={handleSearchChange}
                className={styles.searchInput}
                sx={{
                    "& .MuiInputBase-input::placeholder": { textAlign: "right" },
                    "& .MuiInputBase-input": { textAlign: "right" },
                    borderRadius: "10px",
                }}
                InputProps={{
                    endAdornment: <Search style={{ marginRight: "8px", color: "#888" }} />,
                    startAdornment: (
                        <FormControl size="small">
                            <Select
                                size="small"
                                displayEmpty
                                value={selectedFilters.tripNumber || ""}
                                onChange={(e) => {
                                    const selectedOption = tripNumberOptions.find(
                                        (opt) => opt.value === e.target.value
                                    );
                                    handleFilterChange(
                                        "tripNumber",
                                        e.target.value,
                                        selectedOption?.label || ""
                                    );
                                    setSearch("");
                                }}
                                style={{
                                    height: "35px",
                                    width: "120px",
                                    backgroundColor: "#FFFFFF",
                                }}
                                renderValue={(selected) =>
                                    getLabel(tripNumberOptions, selected, "اختر")
                                }
                            >
                                <MenuItem value="">اختر</MenuItem>
                                {tripNumberOptions.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ),
                }}
            />
        </div>
    );
};

export default FilterBar;
